import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import Spinner from "Common/Globle/Spinner";
import { getAssignedRosterDetail } from "adminApi/DutyRosterApi/AssignedRosterApi";

const ViewAssignedRoster = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const res = await getAssignedRosterDetail(id);
                if (res.success) {
                    setData(res.data);
                }
            } catch (error) {
                console.error("Error fetching roster details:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    if (loading) return <Spinner isSpinner={true} />;

    if (!data) return (
        <PageBody card={true}>
            <div className="text-center py-5">
                <i className="fa fa-exclamation-circle fs-1 text-muted mb-3"></i>
                <h5>Roster Details Not Found</h5>
                <button className="btn btn-primary mt-3" onClick={() => navigate(-1)}>Back to List</button>
            </div>
        </PageBody>
    );

    const { roster, employees } = data;

    const getInitials = (name) => {
        if (!name) return "??";
        return name.split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase();
    };

    return (
        <PageBody
            card={true}
            headerchildren={
                <div className="d-flex align-items-center justify-content-between w-100">
                    <HeadingOne>Assigned Roster View</HeadingOne>
                    <div className="d-flex gap-2">
                        <button className="btn btn-primary" onClick={() => navigate(-1)}>
                            Back
                        </button>
                    </div>
                </div>
            }
        >
            {/* Roster Information Summary */}
            <div className="col-12">
                <div className="roster-detail-header">
                    <div className="row g-4">
                        <div className="col-md-4">
                            <span className="info-label">Current Shift</span>
                            <div className="d-flex align-items-center">
                                <h4 className="info-value me-2">{roster.shift_name}</h4>
                                <span className="shift-tag">Active</span>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <span className="info-label">Rotation Start</span>
                            <h4 className="info-value">{roster.start_date}</h4>
                        </div>
                        <div className="col-md-4">
                            <span className="info-label">Rotation End</span>
                            <h4 className="info-value">{roster.end_date}</h4>
                        </div>
                    </div>
                </div>
            </div>

            {/* Assigned Crew List */}
            <div className="col-12 mt-2">
                <div className="mb-3 px-1">
                    <h6 className="fw-bold mb-0">Assigned Crew ({employees.length})</h6>
                </div>

                <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xxl-5 g-3">
                    {employees.length > 0 ? (
                        employees.map((emp, index) => {
                            const varIndex = (index % 6) + 1;
                            return (
                                <div key={index} className="col">
                                    <div className="roster-member-card d-flex align-items-center">
                                        <div className={`member-avatar color-bg-${varIndex}`}>
                                            {getInitials(emp.employee_name)}
                                        </div>
                                        <div className="member-info">
                                            <h6 className="member-name">{emp.employee_name}</h6>
                                            <span className="member-id">EMP ID: #{emp.employee_id}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="col-12">
                            <div className="alert bg-light border-0 text-center py-4 rounded-4">
                                <i className="fa fa-user-clock opacity-25 d-block mb-2 fs-2"></i>
                                <span className="text-muted">No staff members assigned to this rotation.</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Simplified Internal CSS for Avatar Backgrounds matching the variants in _general.scss if needed */}
            <style dangerouslySetInnerHTML={{
                __html: `
                .color-bg-1 { background: var(--light-bg1); color: #0369a1; }
                .color-bg-2 { background: var(--light-bg2); color: #92400e; }
                .color-bg-3 { background: var(--light-bg3); color: #166534; }
                .color-bg-4 { background: var(--light-bg4); color: #7e22ce; }
                .color-bg-5 { background: var(--light-bg5); color: #991b1b; }
                .color-bg-6 { background: var(--light-bg6); color: #475569; }
            `}} />
        </PageBody>
    );
};

export default ViewAssignedRoster;
